import { findField } from '../../format/elements';
import type { Element } from '../../format/form-definition';
import type { Validation, ValidationKind } from '../../format/pieces';
import { sameJson } from '../same-json';
import { updateElement, withKey, withoutUndefined } from './tree';
import { changed, EditFunctions } from './editContracts';

export type ValidationsEdit =
  | { kind: 'setValidation'; fieldId: string; validation: Validation }
  | { kind: 'removeValidation'; fieldId: string; validationKind: ValidationKind };

export const validationsEdits: EditFunctions<ValidationsEdit> = {
  setValidation: (form, { fieldId, validation }) => {
    const field = findField(form, fieldId);
    if (!field) return changed(form);
    const written = withoutUndefined({ ...validation, message: validation.message || undefined });
    const validations = withValidation(field.validations ?? [], written);
    if (sameJson(validations, field.validations)) return changed(form);
    return changed(updateElement(form, fieldId, element => withValidations(element, validations)));
  },

  removeValidation: (form, { fieldId, validationKind }) => {
    const field = findField(form, fieldId);
    if (!field?.validations?.some(validation => validation.kind === validationKind)) return changed(form);
    const validations = field.validations.filter(validation => validation.kind !== validationKind);
    return changed(updateElement(form, fieldId, element => withValidations(element, validations)));
  },
};

const withValidations = (element: Element, validations: Validation[]): Element => {
  return withKey(element, 'validations', validations.length > 0 ? validations : undefined);
};

const withValidation = (validations: Validation[], validation: Validation): Validation[] => {
  if (validations.some(other => other.kind === validation.kind))
    return validations.map(other => (other.kind === validation.kind ? validation : other));
  const rank = (kind: ValidationKind) => WRITE_ORDER.indexOf(kind);
  const before = validations.findIndex(other => rank(other.kind) > rank(validation.kind));
  const at = before < 0 ? validations.length : before;
  return validations.toSpliced(at, 0, validation);
};

const WRITE_ORDER = ['required', 'pattern', 'range', 'unique'] as const satisfies ValidationKind[];
